import { noop, omit } from 'lodash';
import React, { useState, useEffect } from 'react';
import { TEXT_ROOT } from '../../../propTypes';
import { SVGTransformer } from '../../../lib/SVGTransformer';

import { TEXT_HORIZONTAL_GROW_ATTRIBUTE } from '../../../lib/calculateGrowDimensions/constants';
import { getLayerProps, calculateGrowDimensions, addPixels } from '../../../lib';
import SVGRoot from '../SVGRoot';
import { GROUP_EXCLUDED_ATTRIBUTES } from './constants';

const SVGTextRoot = ({ layers = [], layerRole, fontUrl, onUpdate = noop, onRenderTick = noop, ...svgAttribute }) => {
    const [paths, setPaths] = useState([]);
    const [viewBox, setViewBox] = useState(svgAttribute.viewBox);

    const [textLayer] = layers;
    const text = textLayer.children;

    const { width = 0, left = 0 } = { ...svgAttribute, ...svgAttribute.style };
    const {
        fontSize,
        letterSpacing,
        curvatureAngle,
        [TEXT_HORIZONTAL_GROW_ATTRIBUTE]: growDirection,
    } = { ...textLayer, ...textLayer.style };

    const afterDraw = (metrics, dimensions) => {
        const rootUpdate = {
            ...dimensions,
            layerId: svgAttribute.layerId,
            viewBox: metrics.viewBox,
        };

        onUpdate({ root: rootUpdate });
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const draw = async () => {
            const { paths, metrics } = await SVGTransformer.textToPath({
                text,
                fontUrl,
                fontSize,
                letterSpacing,
                curvatureAngle,
            });
            const metricsViewBox = metrics.viewBox;

            // Setting root new dimensions
            const dimensions = calculateGrowDimensions({
                growDirection,
                anchorStyle: { width, left },
                metrics,
            });
            const dimensionsUpdate = { x: dimensions.left, style: addPixels(dimensions) };

            setPaths(paths);
            setViewBox(metricsViewBox);

            afterDraw(metrics, dimensionsUpdate);

            const hasChanges = width !== `${dimensions.width}px` || left !== `${dimensions.left}px`;

            if (!hasChanges) {
                onRenderTick(layerRole);
            }
        };

        const shouldDraw = !!width && !!left;
        shouldDraw && draw();
    }, [text, fontUrl, fontSize, letterSpacing, curvatureAngle, growDirection, width, left]);

    const groupProps = omit(getLayerProps(textLayer), GROUP_EXCLUDED_ATTRIBUTES);
    const rootAttributes = getLayerProps(svgAttribute);

    const textRootLayers = [
        {
            tag: 'g',
            layerId: `${svgAttribute.layerId}_g`,
            ...groupProps,
            style: textLayer.style,
            childElements: paths.map(({ id, d, transform }) => ({
                tag: 'path',
                layerId: id,
                d,
                transform,
            })),
        },
    ];

    return <SVGRoot {...rootAttributes} viewBox={viewBox} layers={textRootLayers} />;
};

SVGTextRoot.propTypes = TEXT_ROOT;

export default SVGTextRoot;
